import clsx from "clsx";
import {
  ChangeEvent,
  InputHTMLAttributes,
  SyntheticEvent,
  useMemo,
  useState,
} from "react";
import { LuEye, LuEyeOff } from "react-icons/lu";
import { withMask } from "use-mask-input";

interface IProps {
  label: string;
  value: string;
  name: string;
  unit?: string;
  placeholder?: string;
  required?: boolean;
  error?: string | boolean;
  type?: "text" | "phone" | "verifyCode" | "dob" | "email" | "date";
  // Underlying html input type (e.g. "password" to obscure the value)
  inputType?: string;
  // Renders a show/hide toggle that switches inputType between its value and "text"
  withVisibilityToggle?: boolean;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: SyntheticEvent<HTMLInputElement>) => void;
}

const InputField = ({
  label,
  value,
  unit,
  name,
  required,
  error,
  type = "text",
  inputType = "text",
  withVisibilityToggle = false,
  onChange,
  onBlur,
  placeholder,
}: IProps) => {
  const [isRevealed, setIsRevealed] = useState(false);

  const resolvedInputType =
    withVisibilityToggle && isRevealed ? "text" : inputType;
  const typeFieldProps: InputHTMLAttributes<HTMLInputElement> = useMemo(() => {
    if (type === "phone") {
      return {
        ref: withMask("(999) 999-9999", {
          jitMasking: true,
          showMaskOnHover: false,
        }),
        inputMode: "tel",
        pattern: "[0-9]*",
      };
    }
    if (type === "verifyCode") {
      return {
        ref: withMask("9999", {
          jitMasking: true,
          showMaskOnHover: false,
        }),
        inputMode: "numeric",
        pattern: "[0-9]*",
      };
    }
    if (type === "dob" || type === "date") {
      return {
        ref: withMask("99/99/9999", {
          jitMasking: true,
          showMaskOnHover: false,
        }),
        inputMode: "numeric",
        pattern: "[0-9]*",
      };
    }
    return {};
  }, [type]);

  return (
    <div className={clsx("w-full flex flex-col")} data-testid="input-root">
      <label
        className={clsx(
          "text-sm mb-1 text-left font-roboto",
          error && "text-red"
        )}
      >
        {label}
      </label>
      <div className="relative flex items-center">
        <input
          {...typeFieldProps}
          value={value || ""}
          name={name}
          onChange={onChange}
          onBlur={onBlur}
          type={resolvedInputType}
          className={clsx(
            "p-2 sm:p-3 font-roboto !text-base !sm:text-lg border-[3px] w-full border-soft rounded-lg focus:border-medium-green focus:outline-none",
            withVisibilityToggle && "pr-11",
            error && "border-red border-[1px]"
          )}
          required={required}
          placeholder={placeholder}
        />
        {withVisibilityToggle && (
          <button
            type="button"
            onClick={() => setIsRevealed((prev) => !prev)}
            aria-label={isRevealed ? "Hide value" : "Show value"}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
          >
            {isRevealed ? <LuEyeOff size={20} /> : <LuEye size={20} />}
          </button>
        )}
        {unit && (
          <div className="absolute right-1 pr-5 pl-2 py-2 bg-white">{unit}</div>
        )}
      </div>
      {/* if error is boolean don't show error message */}
      {error && typeof error === "string" && (
        <span className="text-left mt-2	text-red text-sm">{error}</span>
      )}
    </div>
  );
};

export { InputField };
